import { useCallback, useEffect, useMemo, useState } from 'react';
import moment, { Moment } from 'moment';
import { useRoomCalendarAvailabilityForRange } from '@features/rooms';
import { toDayDataMap } from './toDayDataMap';

const DATE_FORMAT = 'YYYY-MM-DD';

type Params = {
  adults: number;
  children: number;
  initialMonth?: string;
};

type Range = {
  start: Moment;
  end: Moment;
};

const monthStart = (input?: string) => (input ? moment(input) : moment()).startOf('month');

const initialRange = (input?: string): Range => {
  const start = monthStart(input);
  return { start, end: start.clone().add(1, 'month').endOf('month') };
};

export function useJourneyPickerAvailability({ adults, children, initialMonth }: Params) {
  const [range, setRange] = useState<Range>(() => initialRange(initialMonth));

  useEffect(() => {
    if (!initialMonth) return;
    const target = monthStart(initialMonth);
    setRange((current) =>
      target.isSame(current.start, 'month') ? current : initialRange(initialMonth)
    );
  }, [initialMonth]);

  const startDate = range.start.format(DATE_FORMAT);
  const endDate = range.end.format(DATE_FORMAT);

  const { data: availabilityRecords, isFetching } = useRoomCalendarAvailabilityForRange({
    startDate,
    endDate,
    adults,
    children,
  });

  const availability = useMemo(
    () => toDayDataMap(availabilityRecords ?? []),
    [availabilityRecords]
  );

  const handleMonthChange = useCallback((next: Date) => {
    const target = moment(next).startOf('month');
    setRange((current) => {
      const nextEnd = target.clone().add(1, 'month').endOf('month');
      if (target.isBefore(current.start)) return { start: target, end: current.end };
      if (nextEnd.isAfter(current.end)) return { start: current.start, end: nextEnd };
      return current;
    });
  }, []);

  return {
    availability,
    isLoadingAvailability: isFetching,
    onMonthChange: handleMonthChange,
  };
}
